/**
 * DailyPullJobsPanel — UI dla kolejki dziennego pobierania faktur z KSeF.
 *
 * 3 widoki w jednym dialogu:
 *   1. Lista — 5 ostatnich jobów, przycisk „Nowa kolejka"
 *   2. Formularz tworzenia — date_from, date_to, pause_minutes
 *   3. Panel postępu — lista dni, akcje Start/Pause/Resume/Cancel/Refresh
 *
 * Kolejka działa po stronie BACKENDU (worker `_worker_loop` w
 * `routers/ksef/pull_jobs.py`). Frontend nie steruje już pętlą — wysyła
 * tylko komendy (Start/Pause/Resume/Cancel) i pollinguje status co 5s.
 * Postęp jest zapisywany w MongoDB, więc zamknięcie przeglądarki nie
 * przerywa pobierania.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CalendarRange, Plus, Play, Pause as PauseIcon, X, RefreshCw, AlertCircle,
  CheckCircle2, Clock, Loader2, ChevronLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { logger } from "../../utils/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pl-PL", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const StatusBadge = ({ status }) => {
  const map = {
    pending:   { label: "Oczekuje",      cls: "bg-gray-100 text-gray-700 border-gray-300" },
    running:   { label: "W trakcie",     cls: "bg-blue-100 text-blue-800 border-blue-300" },
    paused:    { label: "Wstrzymany",    cls: "bg-amber-100 text-amber-800 border-amber-300" },
    waiting:   { label: "Limit KSeF",    cls: "bg-amber-100 text-amber-900 border-amber-400" },
    completed: { label: "Zakończony",    cls: "bg-green-100 text-green-800 border-green-300" },
    failed:    { label: "Błąd",          cls: "bg-red-100 text-red-800 border-red-300" },
    cancelled: { label: "Anulowany",     cls: "bg-gray-100 text-gray-500 border-gray-300" },
    skipped:   { label: "Pominięty",     cls: "bg-gray-50 text-gray-500 border-gray-200" },
  };
  const cfg = map[status] || { label: status, cls: "bg-gray-100 text-gray-700" };
  return <Badge variant="outline" className={`${cfg.cls} text-xs`}>{cfg.label}</Badge>;
};

// =====================
// Create form — krok 2
// =====================
const CreateForm = ({ onCreated, onCancel }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState(today);
  const [pauseMinutes, setPauseMinutes] = useState(1);
  const [creating, setCreating] = useState(false);

  const handleSubmit = async () => {
    if (!dateFrom) {
      toast.error("Podaj datę OD");
      return;
    }
    if (new Date(dateFrom) > new Date(dateTo || today)) {
      toast.error("Data OD musi być wcześniejsza lub równa Dacie DO");
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(
        `${API}/ksef/inbox/pull-jobs`,
        {
          date_from: dateFrom,
          date_to: dateTo || today,
          pause_minutes: pauseMinutes,
        },
        { withCredentials: true }
      );
      toast.success(`Kolejka utworzona: ${res.data.total_days} dni`);
      onCreated(res.data.job_id);
    } catch (e) {
      logger.error("Create job error", e);
      toast.error(e.response?.data?.detail || "Nie udało się utworzyć kolejki");
    } finally {
      setCreating(false);
    }
  };

  const days = dateFrom && dateTo
    ? Math.max(0, Math.floor((new Date(dateTo) - new Date(dateFrom)) / 86400000) + 1)
    : 0;

  return (
    <div className="space-y-4" data-testid="daily-pull-create-form">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Pobieraj od dnia</label>
        <Input
          type="date"
          value={dateFrom}
          max={today}
          onChange={(e) => setDateFrom(e.target.value)}
          data-testid="daily-pull-date-from"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Do dnia (domyślnie dziś)</label>
        <Input
          type="date"
          value={dateTo}
          max={today}
          onChange={(e) => setDateTo(e.target.value)}
          data-testid="daily-pull-date-to"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Pauza między dniami (min)</label>
        <Input
          type="number"
          min={1}
          max={120}
          step={1}
          value={pauseMinutes}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              // Pusta wartość — utrzymujemy state na 1 (minimum). Wysyłka
              // pustej wartości do backendu jest zablokowana Pydanticem
              // (`ge=1`), ale zabezpieczamy również UI.
              setPauseMinutes(1);
              return;
            }
            const n = parseInt(raw, 10);
            if (Number.isNaN(n)) return; // nie-liczba — ignoruj
            setPauseMinutes(Math.max(1, Math.min(120, n)));
          }}
          onBlur={(e) => {
            // Normalizacja wyświetlania: Chrome dla input[type=number] pozostawia
            // literalny string wpisany przez usera (np. "01") dopóki React
            // state=1 nie zmieni się. Po utracie fokusa forsujemy DOM value
            // do wartości ze state — user zobaczy "1", nie "01".
            const stateStr = String(pauseMinutes);
            if (e.target.value !== stateStr) {
              e.target.value = stateStr;
            }
          }}
          data-testid="daily-pull-pause"
        />
        <p className="text-xs text-gray-500 mt-1" data-testid="daily-pull-pause-hint">
          Zalecane: 1 minuta. Krótsze odstępy mogą zwiększyć ryzyko osiągnięcia limitów KSeF.
        </p>
      </div>

      {days > 0 && (
        <div className="text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded-md p-3" data-testid="daily-pull-summary">
          Utworzymy kolejkę {days} dni. Między kolejnymi dniami przerwa{" "}
          <span className="font-semibold">{pauseMinutes} min.</span>. Łączny czas pobierania:{" "}
          <span className="font-semibold">
            {(() => {
              // Łączny czas pauz między dniami: (days - 1) * pauseMinutes
              // Dla 1 dnia — brak pauz. Poniżej 60 min — pokazujemy „X min.",
              // powyżej — „~Xh".
              if (days <= 1) return "< 1 min.";
              const totalMin = (days - 1) * pauseMinutes;
              if (totalMin < 60) return `~${totalMin} min.`;
              const h = Math.round(totalMin / 60 * 10) / 10;
              return `~${h}h`;
            })()}
          </span>{" "}
          + czas pobrania KSeF. Już pobrane faktury zostaną rozpoznane (dedup po numerze KSeF) i nie zostaną zdublowane.
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={creating} className="flex-1" data-testid="daily-pull-back-btn">
          <ChevronLeft className="w-4 h-4 mr-1" /> Wróć
        </Button>
        <Button onClick={handleSubmit} disabled={creating || !dateFrom} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="daily-pull-submit-btn">
          {creating ? "Tworzę…" : "Utwórz kolejkę pobierania"}
        </Button>
      </div>
    </div>
  );
};

// =====================
// Job detail panel — krok 3
// =====================
const JobDetailPanel = ({ jobId, onBack, onJobChanged }) => {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionInFlight, setActionInFlight] = useState(false);
  const [now, setNow] = useState(Date.now());
  const pollRef = useRef(null);
  // Overlap-guard: jeśli poprzedni fetchJob jeszcze nie wrócił (KSeF potrafi
  // odpowiadać wolniej niż interval 5s), pomijamy kolejny tick zamiast go
  // stackować. Zapobiega nakładającym się requestom i race'om na setJob.
  const isFetchingRef = useRef(false);

  // Statusy TERMINALNE — zgodne z backendem (routers/ksef/pull_jobs.py:523).
  // Dla nich worker już nie żyje i job nie zmienia stanu → polling zbędny.
  // Wszystkie pozostałe statusy (pending, running, paused, waiting, skipped)
  // są nie-terminalne i wymagają odpytywania — w tym `waiting`, w którym
  // backend czeka na wygaśnięcie rate-limitu KSeF i wraca do running.
  const TERMINAL_STATUSES = ["completed", "cancelled", "failed"];

  const fetchJob = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      const res = await axios.get(`${API}/ksef/inbox/pull-jobs/${jobId}`, { withCredentials: true });
      setJob(res.data);
    } catch (e) {
      logger.error("Fetch job error", e);
      toast.error("Nie udało się pobrać statusu kolejki");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [jobId]);

  useEffect(() => { fetchJob(); }, [fetchJob]);

  // Auto-poll co 5s dla każdego statusu NIE-terminalnego. Kluczowe: `waiting`
  // (rate-limit KSeF) też musi odpytywać, żeby licznik `pause_until` był
  // aktualizowany i banner znikał od razu po powrocie workera do running.
  useEffect(() => {
    if (!job) return;
    if (TERMINAL_STATUSES.includes(job.status)) return;
    pollRef.current = setInterval(fetchJob, 5_000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.status, fetchJob]);

  // Tick co 1s — odświeżenie countdown'a "next_allowed_at" (informacyjny;
  // frontend nie reaguje na końcówkę odliczania, robi to worker w backendzie).
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const callAction = useCallback(async (action, successMsg, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    if (actionInFlight) return;
    setActionInFlight(true);
    try {
      const res = await axios.post(
        `${API}/ksef/inbox/pull-jobs/${jobId}/${action}`,
        {},
        { withCredentials: true },
      );
      if (successMsg) toast.success(successMsg);
      // Niektóre endpointy (start/resume) zwracają {job: {...}} — zaktualizuj
      // od razu, by user nie czekał na kolejny tick polling'u.
      if (res.data?.job) setJob(res.data.job);
      else await fetchJob();
      onJobChanged?.();
    } catch (e) {
      logger.error(`${action} error`, e);
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : `Nie udało się: ${action}`);
    } finally {
      setActionInFlight(false);
    }
  }, [actionInFlight, jobId, fetchJob, onJobChanged]);

  const handleStart  = () => callAction("start",  "Worker uruchomiony — kolejka działa w tle");
  const handlePause  = () => callAction("pause",  "Kolejka wstrzymana");
  const handleResume = () => callAction("resume", "Kolejka wznowiona");
  const handleCancel = () => callAction("cancel", "Kolejka anulowana",
    "Anulować kolejkę? Pobrane faktury zostaną zachowane.");

  if (loading || !job) {
    return <div className="text-center py-8 text-gray-500"><Loader2 className="w-6 h-6 animate-spin inline mr-2" />Wczytywanie…</div>;
  }

  // === Countdown: dwa niezależne mechanizmy, rate-limit ma pierwszeństwo ===
  //
  // 1) `pause_until` — pauza techniczna po HTTP 429 z KSeF (rate limit).
  //    Backend ustawia w statusie `waiting`, po wygaśnięciu resetuje do "".
  //    Frontend interpoluje lokalnie między pollami (tick 1s dla `now`),
  //    a co 5s pobiera świeżą wartość `pause_until` (patrz polling useEffect).
  //
  // 2) `next_allowed_at` — zwykła pauza między dniami (`pause_minutes`).
  //    Ustawiane w statusie `running` po sukcesie dnia; NIE miksujemy tego
  //    z rate-limitem — banner rate-limit renderujemy niezależnie i ma on
  //    pierwszeństwo (banner pauzy dziennej gaszony przez `rateLimitSec===0`).
  //
  // Używamy `Math.ceil` — dla countdown użytkownik nie powinien widzieć "0s"
  // przez ~999ms zanim wartość zniknie; ceil daje spójne "1s → 0 (banner off)".
  let rateLimitSec = 0;
  if (job.pause_until) {
    const dt = Date.parse(job.pause_until);
    if (Number.isFinite(dt)) {
      rateLimitSec = Math.max(0, Math.ceil((dt - now) / 1000));
    }
  }
  let waitSec = 0;
  if (job.next_allowed_at && rateLimitSec === 0) {
    const dt = Date.parse(job.next_allowed_at);
    if (Number.isFinite(dt)) {
      waitSec = Math.max(0, Math.ceil((dt - now) / 1000));
    }
  }
  // Banner rate-limit widoczny gdy: (a) pause_until niepuste,
  // (b) czas > 0. Gdy backend wyczyści pause_until (przejście waiting→running),
  // rateLimitSec = 0 → banner znika bez opóźnienia (najbliższy poll odświeży).
  const showRateLimitBanner = rateLimitSec > 0 && !!job.pause_until;
  const showWaitBanner = waitSec > 0 && rateLimitSec === 0;

  const isTerminal = TERMINAL_STATUSES.includes(job.status);
  const isRunning  = job.status === "running";
  const isPaused   = job.status === "paused";
  const isPending  = job.status === "pending";
  const isWaiting  = job.status === "waiting";
  const canCancel  = !isTerminal;

  return (
    <div className="space-y-3" data-testid="daily-pull-job-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-sm text-blue-600 hover:underline flex items-center" data-testid="daily-pull-detail-back-btn">
          <ChevronLeft className="w-4 h-4 mr-1" /> Lista kolejek
        </button>
        <StatusBadge status={job.status} />
      </div>

      {/* Summary card */}
      <Card className="p-3 space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">Zakres:</span>
          <span className="font-mono">{job.date_from} → {job.date_to}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Postęp:</span>
          <span className="font-semibold">{job.processed_days} / {job.total_days} dni</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Nowych faktur:</span>
          <span className="font-mono text-green-700 font-semibold" data-testid="daily-pull-total-new">{job.total_new_invoices}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Już istniało:</span>
          <span className="font-mono text-gray-700">{job.total_existing_invoices}</span>
        </div>
        {job.total_duplicate_invoices > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-500">Duplikaty/pominięte:</span>
            <span className="font-mono text-amber-700">{job.total_duplicate_invoices}</span>
          </div>
        )}
        {job.total_errors > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-500">Błędy:</span>
            <span className="font-mono text-red-700">{job.total_errors}</span>
          </div>
        )}
        <div className="flex justify-between text-xs text-gray-500 border-t pt-2">
          <span>Pauza:</span>
          <span>{job.pause_minutes} min.</span>
        </div>
      </Card>

      {/* Rate-limit KSeF (429) — informacyjny banner. Job w statusie
          `waiting`, backend automatycznie wznowi po upływie pause_until. */}
      {showRateLimitBanner && (
        <Card
          className="p-3 bg-amber-50 border-amber-300 flex items-start gap-2 text-sm text-amber-900"
          data-testid="daily-pull-rate-limit-bar"
        >
          <Clock className="w-4 h-4 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold">Limit KSeF</div>
            <div>
              Następna próba za{" "}
              <span className="font-mono font-semibold" data-testid="daily-pull-rate-limit-countdown">
                {Math.floor(rateLimitSec / 60)} min {rateLimitSec % 60} s
              </span>
              .
              <br />
              Po upływie tego czasu dokumenty będą ponownie pobierane automatycznie.
            </div>
          </div>
        </Card>
      )}

      {/* Pause countdown */}
      {showWaitBanner && (
        <Card className="p-3 bg-amber-50 border-amber-200 flex items-center gap-2 text-sm text-amber-900" data-testid="daily-pull-wait-bar">
          <Clock className="w-4 h-4 shrink-0" />
          <span>
            Pauza między dniami — następne pobranie za {" "}
            <span className="font-mono font-semibold">
              {Math.floor(waitSec / 60)}m {waitSec % 60}s
            </span>
          </span>
        </Card>
      )}

      {/* Last error — ukrywamy komunikaty rate-limit ("Rate limit KSeF: ..."),
          bo ta informacja jest już prezentowana w żółtym banerze "Limit KSeF"
          powyżej. Inne błędy nadal wyświetlamy. */}
      {job.last_error && !job.last_error.startsWith("Rate limit KSeF:") && (
        <Card className="p-3 bg-red-50 border-red-200 text-xs text-red-800 flex items-start gap-2" data-testid="daily-pull-last-error">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{job.last_error}</span>
        </Card>
      )}

      {/* Info: kolejka pracuje w tle */}
      {!isTerminal && (
        <Card className="p-3 bg-indigo-50 border-indigo-200 text-xs text-indigo-900 flex items-start gap-2" data-testid="daily-pull-backend-info">
          <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold">Kolejka działa w tle.</div>
            <div>
              Możesz zamknąć przeglądarkę.
              <br />
              Postęp zostanie zapisany.
            </div>
          </div>
        </Card>
      )}

      {/* Actions — Start / Pause / Resume / Cancel */}
      <div className="flex flex-col sm:flex-row gap-2">
        {(isPending || isPaused) && (
          <Button
            size="sm"
            onClick={isPaused ? handleResume : handleStart}
            disabled={actionInFlight}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex-1"
            data-testid={isPaused ? "daily-pull-resume-btn" : "daily-pull-start-btn"}
          >
            <Play className="w-4 h-4 mr-2" />
            {actionInFlight ? "Wysyłam…" : (isPaused ? "Wznów" : "Start")}
          </Button>
        )}
        {isRunning && (
          <Button
            size="sm"
            onClick={handlePause}
            disabled={actionInFlight}
            className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 flex-1"
            data-testid="daily-pull-pause-btn"
          >
            <PauseIcon className="w-4 h-4 mr-2" />
            {actionInFlight ? "Wysyłam…" : "Pauza"}
          </Button>
        )}
        {isWaiting && (
          <Button
            size="sm"
            onClick={handleResume}
            disabled={actionInFlight}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex-1"
            data-testid="daily-pull-retry-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {actionInFlight ? "Wysyłam…" : "Ponów teraz"}
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={fetchJob}
          disabled={actionInFlight}
          data-testid="daily-pull-refresh-btn"
          aria-label="Odśwież status"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
        {canCancel && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleCancel}
            disabled={actionInFlight}
            className="text-red-700 border-red-300 hover:bg-red-50"
            data-testid="daily-pull-cancel-btn"
          >
            <X className="w-4 h-4 mr-1" /> Anuluj
          </Button>
        )}
      </div>

      {/* Days list */}
      <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="daily-pull-days-list">
        {(job.days || []).map((day) => (
          <div
            key={day.date}
            className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs"
            data-testid={`daily-pull-day-${day.date}`}
          >
            <div className="flex items-center gap-2">
              {day.status === "completed" && <CheckCircle2 className="w-4 h-4 text-green-600" />}
              {day.status === "running" && <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />}
              {day.status === "failed" && <AlertCircle className="w-4 h-4 text-red-600" />}
              {day.status === "pending" && <Clock className="w-4 h-4 text-gray-400" />}
              <span className="font-mono">{day.date}</span>
            </div>
            <div className="flex items-center gap-3 text-right">
              {day.status === "completed" && (
                <span className="text-gray-600">
                  <span className="text-green-700 font-semibold">+{day.new_invoices}</span>
                  {day.existing_invoices > 0 && <span className="text-gray-500"> / {day.existing_invoices} ex.</span>}
                  {day.pages_fetched > 1 && <span className="text-blue-600 ml-1">[{day.pages_fetched} str.]</span>}
                </span>
              )}
              {day.status === "failed" && (
                <span className="text-red-600 max-w-[140px] truncate" title={day.error}>{day.error?.substring(0, 30)}</span>
              )}
              <StatusBadge status={day.status} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// =====================
// List view — krok 1
// =====================
const JobsListView = ({ onCreate, onSelect, refreshKey }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ksef/inbox/pull-jobs?limit=10`, { withCredentials: true });
      setItems(res.data.items || []);
    } catch (e) {
      logger.error("Fetch jobs list error", e);
      toast.error("Nie udało się pobrać listy kolejek");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList, refreshKey]);

  return (
    <div className="space-y-3" data-testid="daily-pull-jobs-list">
      <Button onClick={onCreate} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="daily-pull-new-btn">
        <Plus className="w-4 h-4 mr-2" /> Nowa kolejka pobierania
      </Button>

      <div className="space-y-2 max-h-80 overflow-y-auto">
        {loading ? (
          <div className="text-center py-6 text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Wczytywanie…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            Brak kolejek. Utwórz pierwszą używając przycisku powyżej.
          </div>
        ) : (
          items.map((j) => (
            <button
              key={j.job_id}
              type="button"
              onClick={() => onSelect(j.job_id)}
              className="w-full text-left p-3 border border-gray-200 rounded-md hover:bg-blue-50 hover:border-blue-300 transition-colors"
              data-testid={`daily-pull-job-row-${j.job_id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xs text-gray-700">{j.date_from} → {j.date_to}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {j.processed_days}/{j.total_days} dni · {j.total_new_invoices} nowych · {j.total_existing_invoices} istniało
                  </div>
                  <div className="text-[10px] text-gray-400 mt-0.5">Utworzono: {formatDate(j.created_at)}</div>
                </div>
                <StatusBadge status={j.status} />
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

// =====================
// Main panel — orchestrator widoków
// =====================
const DailyPullJobsPanel = ({ open, onOpenChange, onJobChanged }) => {
  const [view, setView] = useState("list"); // list | create | detail
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Reset state on close
  useEffect(() => {
    if (!open) {
      setView("list");
      setSelectedJobId(null);
    }
  }, [open]);

  const handleCreated = (jobId) => {
    setSelectedJobId(jobId);
    setView("detail");
    onJobChanged?.();
    setRefreshKey((k) => k + 1);
  };

  const handleSelect = (jobId) => {
    setSelectedJobId(jobId);
    setView("detail");
  };

  const handleBackToList = () => {
    setView("list");
    setSelectedJobId(null);
    setRefreshKey((k) => k + 1);
  };

  const handleJobChanged = () => {
    setRefreshKey((k) => k + 1);
    onJobChanged?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="daily-pull-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarRange className="w-5 h-5 text-blue-600" />
            Kolejka dziennego pobierania
          </DialogTitle>
        </DialogHeader>

        {view === "list" && (
          <JobsListView onCreate={() => setView("create")} onSelect={handleSelect} refreshKey={refreshKey} />
        )}

        {view === "create" && (
          <CreateForm onCreated={handleCreated} onCancel={handleBackToList} />
        )}

        {view === "detail" && selectedJobId && (
          <JobDetailPanel jobId={selectedJobId} onBack={handleBackToList} onJobChanged={handleJobChanged} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DailyPullJobsPanel;
